import React from "react";
import { actionsApi } from "../../services/api";
import type {
  NodeAction,
  NodeActionCreate,
  NodeActionType,
  EmailActionConfig,
  SmsActionConfig,
  RunQueryActionConfig,
  EmailAction,
  SmsAction,
  RunQueryAction,
} from "../../types/node_action";
import {
  Trash2,
  Plus,
  ChevronDown,
  ChevronUp,
  Mail,
  MessageSquare,
  Database,
} from "lucide-react";
import { Textarea } from "../ui/Input";

interface Props {
  nodeId: number;
  readOnly?: boolean;
}

export default function NodeActionsSection({
  nodeId,
  readOnly = false,
}: Props) {
  const [collapsed, setCollapsed] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [actions, setActions] = React.useState<NodeAction[]>([]);
  const [emailActions, setEmailActions] = React.useState<EmailAction[]>([]);
  const [smsActions, setSmsActions] = React.useState<SmsAction[]>([]);
  const [runQueryActions, setRunQueryActions] = React.useState<
    RunQueryAction[]
  >([]);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (readOnly) setCollapsed(false);
  }, [readOnly]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        nodeActions,
        emailActionsList,
        smsActionsList,
        runQueryActionsList,
      ] = await Promise.all([
        actionsApi.list(nodeId),
        actionsApi.listEmailActions(),
        actionsApi.listSmsActions(),
        actionsApi.listRunQueryActions(),
      ]);

      setActions(nodeActions.actions);
      setEmailActions(emailActionsList);
      setSmsActions(smsActionsList);
      setRunQueryActions(runQueryActionsList);
    } catch (e: any) {
      setError(e?.message || "Failed to load actions");
    } finally {
      setLoading(false);
    }
  }, [nodeId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const addAction = async () => {
    const type = (
      window.prompt("Action type ('email', 'sms', or 'run_query')", "email") ||
      "email"
    ).trim() as NodeActionType;

    if (type !== "email" && type !== "sms" && type !== "run_query") {
      alert("Invalid action type. Must be 'email', 'sms', or 'run_query'");
      return;
    }

    try {
      const created = await actionsApi.create({
        node_id: nodeId,
        action_type: type,
      });
      setActions((prev) => [...prev, created]);
    } catch (e: any) {
      alert(`Failed to create action: ${e?.message || "Unknown error"}`);
    }
  };

  const removeAction = async (row: NodeAction) => {
    const confirmed = window.confirm(`Delete action?`);
    if (!confirmed) return;
    try {
      await actionsApi.remove(row.id);
      setActions((prev) => prev.filter((a) => a.id !== row.id));
    } catch (e: any) {
      alert(`Failed to delete action: ${e?.message || "Unknown error"}`);
    }
  };

  const getActionDetails = (action: NodeAction) => {
    if (action.action_type === "email" && action.action_ref_id) {
      return emailActions.find((ea) => ea.id === action.action_ref_id) || null;
    } else if (action.action_type === "sms" && action.action_ref_id) {
      return smsActions.find((sa) => sa.id === action.action_ref_id) || null;
    } else if (action.action_type === "run_query" && action.action_ref_id) {
      return (
        runQueryActions.find((rqa) => rqa.id === action.action_ref_id) || null
      );
    }
    return null;
  };

  const getActionIcon = (type: NodeActionType) => {
    switch (type) {
      case "email":
        return <Mail className="h-4 w-4" />;
      case "sms":
        return <MessageSquare className="h-4 w-4" />;
      case "run_query":
        return <Database className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 relative">
      <div className={readOnly ? "pointer-events-none" : undefined}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Actions</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Toggle section"
            >
              {collapsed ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronUp className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
        {!collapsed && (
          <div className="mt-4 space-y-4">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={addAction}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-green-600 text-white rounded"
              >
                <Plus className="h-4 w-4" /> Add Action
              </button>
            </div>
            {loading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : null}
            {error ? <div className="text-sm text-red-600">{error}</div> : null}
            {actions.length === 0 && !loading ? (
              <div className="text-sm text-gray-500">
                No actions configured for this node.
              </div>
            ) : null}
            {actions.map((action) => (
              <ActionRow
                key={action.id}
                action={action}
                actionDetails={getActionDetails(action)}
                onRemove={() => removeAction(action)}
                readOnly={readOnly}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionRow({
  action,
  actionDetails,
  onRemove,
  readOnly,
}: {
  action: NodeAction;
  actionDetails: EmailAction | SmsAction | RunQueryAction | null;
  onRemove: () => void;
  readOnly: boolean;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const [config, setConfig] = React.useState<any>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (actionDetails) {
      // Initialize config with existing action details
      setConfig({
        name: actionDetails.name,
        description: actionDetails.description || "",
        is_active: actionDetails.is_active,
        ...(action.action_type === "email" && {
          to_address: (actionDetails as EmailAction).to_address || "",
          from_email: (actionDetails as EmailAction).from_email || "",
          content: (actionDetails as EmailAction).content || "",
          subject: (actionDetails as EmailAction).subject || "",
          provider_template_code:
            (actionDetails as EmailAction).provider_template_code || "",
          delayed_send_minutes:
            (actionDetails as EmailAction).delayed_send_minutes || 0,
        }),
        ...(action.action_type === "sms" && {
          to_address: (actionDetails as SmsAction).to_address || "",
          sender_id: (actionDetails as SmsAction).sender_id || "",
          content: (actionDetails as SmsAction).content || "",
          provider_template_code:
            (actionDetails as SmsAction).provider_template_code || "",
          delayed_send_minutes:
            (actionDetails as SmsAction).delayed_send_minutes || 0,
        }),
        ...(action.action_type === "run_query" && {
          db_name: (actionDetails as RunQueryAction).db_name || "main",
          sql_text: (actionDetails as RunQueryAction).sql_text || "",
        }),
      });
    } else {
      // Initialize empty config for new actions
      setConfig({
        name: "",
        description: "",
        is_active: true,
        ...(action.action_type === "email" && {
          to_address: "",
          from_email: "",
          content: "",
          subject: "",
          provider_template_code: "",
          delayed_send_minutes: 0,
        }),
        ...(action.action_type === "sms" && {
          to_address: "",
          sender_id: "",
          content: "",
          provider_template_code: "",
          delayed_send_minutes: 0,
        }),
        ...(action.action_type === "run_query" && {
          db_name: "main",
          sql_text: "",
        }),
      });
    }
  }, [action, actionDetails]);

  const saveConfig = async () => {
    if (!config) return;

    setSaving(true);
    try {
      if (action.action_type === "email") {
        const emailConfig: EmailActionConfig = {
          name: config.name,
          description: config.description,
          is_active: config.is_active,
          to_address: config.to_address,
          from_email: config.from_email,
          content: config.content,
          subject: config.subject,
          provider_template_code: config.provider_template_code,
          delayed_send_minutes: config.delayed_send_minutes,
        };
        await actionsApi.setEmailConfig(action.id, emailConfig);
      } else if (action.action_type === "sms") {
        const smsConfig: SmsActionConfig = {
          name: config.name,
          description: config.description,
          is_active: config.is_active,
          to_address: config.to_address,
          sender_id: config.sender_id,
          content: config.content,
          provider_template_code: config.provider_template_code,
          delayed_send_minutes: config.delayed_send_minutes,
        };
        await actionsApi.setSmsConfig(action.id, smsConfig);
      } else if (action.action_type === "run_query") {
        const runQueryConfig: RunQueryActionConfig = {
          name: config.name,
          description: config.description,
          db_name: config.db_name,
          sql_text: config.sql_text,
        };
        await actionsApi.setRunQueryConfig(action.id, runQueryConfig);
      }

      // Reload to get updated data
      window.location.reload();
    } catch (e: any) {
      alert(`Failed to save configuration: ${e?.message || "Unknown error"}`);
    } finally {
      setSaving(false);
    }
  };

  const getActionIcon = (type: NodeActionType) => {
    switch (type) {
      case "email":
        return <Mail className="h-4 w-4" />;
      case "sms":
        return <MessageSquare className="h-4 w-4" />;
      case "run_query":
        return <Database className="h-4 w-4" />;
      default:
        return null;
    }
  };

  if (!config) return null;

  return (
    <div className="border rounded p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {getActionIcon(action.action_type)}
          <div>
            <div className="font-medium">
              {actionDetails?.name || "Unconfigured Action"}{" "}
              <span className="text-xs text-gray-500">
                ({action.action_type})
              </span>
            </div>
            {actionDetails?.description ? (
              <div className="text-xs text-gray-600">
                {actionDetails.description}
              </div>
            ) : null}
            {!actionDetails && (
              <div className="text-xs text-orange-600">
                Action needs configuration
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {actionDetails && (
            <label className="text-xs text-gray-600 inline-flex items-center gap-1">
              <input
                type="checkbox"
                checked={actionDetails.is_active}
                disabled={true}
              />{" "}
              Active
            </label>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs px-2 py-1 border rounded"
          >
            {expanded ? "Hide" : "Configure"}
          </button>
          {!readOnly && (
            <button
              type="button"
              onClick={onRemove}
              className="text-xs inline-flex items-center gap-1 px-2 py-1 border rounded text-red-600"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* Common fields */}
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-gray-600">
              Name
              <input
                className="mt-1 w-full border rounded px-2 py-1 text-sm"
                value={config.name}
                onChange={(e) => setConfig({ ...config, name: e.target.value })}
                disabled={readOnly}
              />
            </label>
            <label className="text-xs text-gray-600">
              Description
              <input
                className="mt-1 w-full border rounded px-2 py-1 text-sm"
                value={config.description}
                onChange={(e) =>
                  setConfig({ ...config, description: e.target.value })
                }
                disabled={readOnly}
              />
            </label>
          </div>

          {/* Email-specific fields */}
          {action.action_type === "email" && (
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-gray-600">
                To Address
                <input
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  value={config.to_address}
                  onChange={(e) =>
                    setConfig({ ...config, to_address: e.target.value })
                  }
                  disabled={readOnly}
                />
              </label>
              <label className="text-xs text-gray-600">
                From Email
                <input
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  value={config.from_email}
                  onChange={(e) =>
                    setConfig({ ...config, from_email: e.target.value })
                  }
                  disabled={readOnly}
                />
              </label>
              <label className="text-xs text-gray-600 col-span-2">
                Subject
                <input
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  value={config.subject}
                  onChange={(e) =>
                    setConfig({ ...config, subject: e.target.value })
                  }
                  disabled={readOnly}
                />
              </label>
              <div className="col-span-2">
                <Textarea
                  label="Content"
                  className="text-sm"
                  rows={3}
                  value={config.content}
                  onChange={(e) =>
                    setConfig({ ...config, content: e.target.value })
                  }
                  disabled={readOnly}
                  variant="rounded-md"
                  showLabel={true}
                />
              </div>
            </div>
          )}

          {/* SMS-specific fields */}
          {action.action_type === "sms" && (
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-gray-600">
                To Phone
                <input
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  value={config.to_address}
                  onChange={(e) =>
                    setConfig({ ...config, to_address: e.target.value })
                  }
                  disabled={readOnly}
                />
              </label>
              <label className="text-xs text-gray-600">
                Sender ID
                <input
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  value={config.sender_id}
                  onChange={(e) =>
                    setConfig({ ...config, sender_id: e.target.value })
                  }
                  disabled={readOnly}
                />
              </label>
              <div className="col-span-2">
                <Textarea
                  label="Content"
                  className="text-sm"
                  rows={3}
                  value={config.content}
                  onChange={(e) =>
                    setConfig({ ...config, content: e.target.value })
                  }
                  disabled={readOnly}
                  variant="rounded-md"
                  showLabel={true}
                />
              </div>
            </div>
          )}

          {/* Run Query specific fields */}
          {action.action_type === "run_query" && (
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-gray-600">
                Database
                <input
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  value={config.db_name}
                  onChange={(e) =>
                    setConfig({ ...config, db_name: e.target.value })
                  }
                  disabled={readOnly}
                />
              </label>
              <div className="col-span-2">
                <Textarea
                  label="SQL Query"
                  className="text-sm font-mono"
                  rows={4}
                  value={config.sql_text}
                  onChange={(e) =>
                    setConfig({ ...config, sql_text: e.target.value })
                  }
                  disabled={readOnly}
                  placeholder="INSERT INTO table (column) VALUES ('{{variable}}')"
                  variant="rounded-md"
                  showLabel={true}
                />
              </div>
            </div>
          )}

          {/* Save button */}
          {!readOnly && (
            <div className="flex justify-end">
              <button
                type="button"
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
                onClick={saveConfig}
                disabled={saving || !config.name}
              >
                {saving ? "Saving..." : "Save Configuration"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
